import React, { Fragment, useState, useEffect, useRef } from "react";
import {
	Card,
	CardBody,
	Col,
	Table,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getTransferAPI } from "./TransferService";
import { useLoader } from "../../_helper/LoaderContext";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { FormLabel } from "react-bootstrap";
import { usePharmacy } from "../../_helper/PharmacyProvider";

const TransferHistory = () => {
	const [transfers, setTransfers] = useState([]);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);

	const { pharmacyPOS } = usePharmacy();
	const { setLoading } = useLoader();
	const { t } = useTranslation();
	const eyeButtonRefs = useRef({});

	const [viewData, setViewData] = useState(null);
	const [viewTooltipOpen, setViewTooltipOpen] = useState({});
	const [viewModal, setViewModal] = useState(false);

	const toggleViewTooltip = (id) => {
		setViewTooltipOpen((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	};

	useEffect(() => {
		fetchTransfers();
	}, [page, size]);

	const fetchTransfers = async () => {
		try {
			setLoading(true);
			const body = { page, limit: size };
			const response = await getTransferAPI(body);
			if (response.data.status === "success") {
				setTransfers(response.data.data.details || []);
				const totalCount = response.data.data.total || 0;
				setTotalItems(Math.ceil(totalCount / size));
			}
		} catch (error) {
			console.error("Error fetching transfers:", error);
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, "0");
		const month = date.toLocaleString(undefined, { month: "short" });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`;
	};

	const getStatusBadge = (status) => {
		if (status === "pending") {
			return <span className="badge bg-warning">{t("Pending")}</span>;
		}
		if (status === "completed") {
			return <span className="badge bg-success">{t("Completed")}</span>;
		}
		if (status === "rejected") {
			return <span className="badge bg-danger">{t("Rejected")}</span>;
		}
		return <span className="badge bg-secondary">{t("Unknown")}</span>;
	};

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseInt(e.target.value));
		setPage(1);
	};

	// 🔹 Helper to determine type (Transferred / Received)
	const getTransferType = (transfer) => {
		const fromPharmacy = transfer.from_pharmacy_details?.[0]?._id;
		const toPharmacy = transfer.to_pharmacy_details?.[0]?._id;

		if (fromPharmacy === pharmacyPOS._id) return t("Transferred");
		if (toPharmacy === pharmacyPOS._id) return t("Received");
		return "-";
	};

	// 🔹 New function for handling view button
	const handleView = (transfer) => {
		if (eyeButtonRefs.current[transfer._id]) {
			eyeButtonRefs.current[transfer._id].blur();
		}
		setViewTooltipOpen((prev) => ({
			...prev,
			[transfer._id]: false,
		}));
		setViewData(transfer);
		setViewModal(true);
	};

	// 🔹 Helper to render pharmacy & clinic display with arrow only for logged-in pharmacy
	const renderPharmacyWithClinic = (pharmacy, clinic, isFrom) => {
		if (!pharmacy && !clinic) return "-";

		// Show arrow only if pharmacy is the logged-in pharmacy
		const showArrow = pharmacy?.pharmacyId === pharmacyPOS._id;
		const arrow = showArrow ? (isFrom ? "up" : "down") : null;

		return (
			<div>
				<span className="fw-bold">
					{pharmacy?.pharmacyName || "-"}{" "}
					{arrow === "up" && <i className="fa fa-arrow-up text-danger ms-1"></i>}
					{arrow === "down" && <i className="fa fa-arrow-down text-success ms-1"></i>}
				</span>
				{clinic?.clinicName && (
					<div className="small text-muted">{clinic.clinicName}</div>
				)}
			</div>
		);
	};

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Manage Transfers")}
				parent={t("Dashboard")}
				title={t("Transfer History")}
			/>

			<Col sm="12">
				<Card>
					<CardBody>
						<div className="table-responsive">
							<Table bordered hover striped>
								<thead>
									<tr>
										<th>#</th>
										<th>{t("From")}</th>
										<th>{t("To")}</th>
										{/* <th>{t("Items")} ({t("Batch No.")})</th> */}
										<th>{t("Items")}</th>
										<th>{t("Type")}</th>
										{/* <th>{t("Status")}</th> */}
										<th>{t("Notes")}</th>
										<th>{t("Date")}</th>
										<th className="text-center">{t("Action")}</th>
									</tr>
								</thead>
								<tbody>
									{transfers.length > 0 ? (
										transfers.map((transfer, idx) => {
											const fromPharmacy = transfer.from_pharmacy_details?.[0];
											const toPharmacy = transfer.to_pharmacy_details?.[0];
											const fromClinic = transfer.from_clinic_admin?.[0];
											const toClinic = transfer.to_clinic_admin?.[0];

											const isSender = fromPharmacy?.pharmacyId === pharmacyPOS._id;
											const isReceiver = toPharmacy?.pharmacyId === pharmacyPOS._id;

											return (
												<tr key={transfer._id}>
													<td>{(page - 1) * size + idx + 1}</td>

													<td>
														{renderPharmacyWithClinic(
															transfer.from_pharmacy_details?.[0],
															transfer.from_clinic_admin?.[0],
															true
														)}
													</td>
													<td>
														{renderPharmacyWithClinic(
															transfer.to_pharmacy_details?.[0],
															transfer.to_clinic_admin?.[0],
															false
														)}
													</td>

													<td>
														<span
															className="truncate-text"
															style={{ display: "inline-block", maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
															title={transfer.transferred_items
																.map((i) => `${i.medicine_name}`)
																.join(", ")}
														>
															{transfer.transferred_items
																.map((i) => `${i.medicine_name}`)
																.join(", ")}
														</span>
													</td>

													{/* 🔹 Type column */}
													<td>
														{isSender
															? t("Transferred")
															: isReceiver
																? t("Received")
																: "-"}
													</td>

													{/* <td>{getStatusBadge(transfer.status)}</td> */}
													<td>
														<span
															className="truncate-text"
															title={transfer.notes || "-"}
														>
															{transfer.notes || "-"}
														</span>
													</td>
													<td>{formatDate(transfer.createdAt)}</td>

													<td className="text-center">
														<button
															className="success-action-button me-1"
															id={`ViewToolTip-${transfer._id}`} // ✅ remove spaces
															ref={(el) => (eyeButtonRefs.current[transfer._id] = el)}
															onClick={() => handleView(transfer)}
														>
															<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
															<ToolTip
																attrToolTip={{
																	placement: "top",
																	isOpen: viewTooltipOpen[transfer._id] || false,
																	target: `ViewToolTip-${transfer._id}`, // ✅ match ID without spaces
																	toggle: () => toggleViewTooltip(transfer._id),
																}}
															>
																<strong>{t("View")}</strong>
															</ToolTip>
														</button>
													</td>
												</tr>
											);
										})
									) : (
										<tr>
											<td colSpan="9" className="p-3 text-center text-muted">
												{t("No transfers found")}
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>

						{/* Pagination */}
						<div className="mt-3 d-md-flex align-items-center justify-content-between">
							<div className="show-entries">
								<FormLabel>{t("Show")}&nbsp;</FormLabel>
								<select
									value={size}
									onChange={handlePageSizeChange}
									className="form-control"
									style={{ display: "inline", width: "auto" }}
								>
									<option value={5}>5</option>
									<option value={10}>10</option>
									<option value={20}>20</option>
								</select>
								&nbsp;<FormLabel>{t("entries")}</FormLabel>
							</div>
							<MyPagination
								totalPages={totalItems}
								currentPage={page}
								onPageChange={handlePageChange}
							/>
						</div>
					</CardBody>
				</Card>
			</Col >

			{/* 🔹 View Modal */}
			{/* 🔹 View Modal */}
			<Modal
				isOpen={viewModal}
				toggle={() => setViewModal(!viewModal)}
				size="lg"
				centered
			>
				<ModalHeader toggle={() => setViewModal(false)}>
					<i className="fa fa-exchange me-2 text-primary"></i>
					{t("Transfer Details")}
				</ModalHeader>
				<ModalBody>
					{viewData && (
						<div className="transfer-details">
							{/* From / To Section */}
							<Row className="mb-4">
								<Col md="6">
									<Card className="shadow-sm border-0 h-100 bg-info-subtle">
										<CardBody>
											<h6 className="fw-bold text-primary mb-2">{t("From")}</h6>
											<p className="mb-1">
												<strong>{t("Clinic")}:</strong>{" "}
												{viewData.from_clinic_admin?.[0]?.clinicName || "-"}
											</p>
											<p className="mb-0">
												<strong>{t("Pharmacy")}:</strong>{" "}
												{viewData.from_pharmacy_details?.[0]?.pharmacyName || "-"}
											</p>
										</CardBody>
									</Card>
								</Col>
								<Col md="6">
									<Card className="shadow-sm border-0 h-100 bg-success-subtle">
										<CardBody>
											<h6 className="fw-bold text-success mb-2">{t("To")}</h6>
											<p className="mb-1">
												<strong>{t("Clinic")}:</strong>{" "}
												{viewData.to_clinic_admin?.[0]?.clinicName || "-"}
											</p>
											<p className="mb-0">
												<strong>{t("Pharmacy")}:</strong>{" "}
												{viewData.to_pharmacy_details?.[0]?.pharmacyName || "-"}
											</p>
										</CardBody>
									</Card>
								</Col>
							</Row>

							{/* Transfer Items */}
							<h6 className="fw-bold mb-2">{t("Transferred Items")}</h6>
							<Table bordered hover striped>
								<thead>
									<tr>
										<th>#</th>
										<th>{t("Medicine Name")}</th>
										<th>{t("Brand Name")}</th>
										<th>{t("Batch No.")}</th>
										<th>{t("Quantity")}</th>
									</tr>
								</thead>
								<tbody>
									{viewData.transferred_items && viewData.transferred_items.length > 0 ? (
										viewData.transferred_items.map((item, idx) => (
											<tr key={idx}>
												<td>{idx + 1}</td>
												<td>
													<span
														className="d-block text-truncate"
														style={{ maxWidth: "130px" }}
														title={item.medicine_name}
													>
														{item.medicine_name}
													</span>
												</td>
												<td>
													{item.salt_composition && item.salt_composition !== "" ? (
														<span
															className="d-block text-truncate"
															style={{ maxWidth: "170px" }}
															title={item.salt_composition}
														>
															{item.salt_composition}
														</span>
													) : "-"}
												</td>
												<td>{item.batch_number}</td>
												<td>{item.transfer_quantity}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="4" className="text-center text-muted">
												{t("No items found")}
											</td>
										</tr>
									)}
								</tbody>
							</Table>

							{/* Status & Notes */}
							<Row className="mt-4">
								<Col md="3" className="mb-3">
									<strong>{t("Status")}:</strong>{" "}<br />
									<span
										className={`text-uppercase mt-2 badge ${viewData.status === "pending"
											? "bg-warning text-dark"
											: viewData.status === "completed"
												? "bg-success"
												: "bg-danger"
											}`}
									>
										{viewData.status}
									</span>
								</Col>
								<Col md="9">
									<strong>{t("Notes")}:</strong>
									<div className="border rounded p-2 bg-body-tertiary mt-1">
										{viewData.notes || t("No notes provided")}
									</div>
								</Col>
							</Row>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setViewModal(false)}>
						<i className="fa fa-times me-1"></i> {t("Close")}
					</Button>
				</ModalFooter>
			</Modal>
			{/* <Modal
				isOpen={viewModal}
				toggle={() => setViewModal(!viewModal)}
				size="lg"
				centered
			>
				<ModalHeader toggle={() => setViewModal(false)}>
					<i className="fa fa-exchange me-2 text-primary"></i>
					{t("Transfer Details")}
				</ModalHeader>
				<ModalBody>
					{viewData && (
						<div className="transfer-details">
							<Row className="mb-4">
								<Col md="6">
									<Card className="shadow-sm border-0 h-100 bg-info-subtle">
										<CardBody>
											<h6 className="fw-bold text-primary mb-2">{t("From")}</h6>
											<p className="mb-1">
												<strong>{t("Clinic")}:</strong>{" "}
												{viewData.from_clinic_admin?.[0]?.clinicName || "-"}
											</p>
											<p className="mb-0">
												<strong>{t("Pharmacy")}:</strong>{" "}
												{viewData.from_pharmacy_details?.[0]?.pharmacyName || "-"}
											</p>
										</CardBody>
									</Card>
								</Col>
								<Col md="6">
									<Card className="shadow-sm border-0 h-100 bg-success-subtle">
										<CardBody>
											<h6 className="fw-bold text-success mb-2">{t("To")}</h6>
											<p className="mb-1">
												<strong>{t("Clinic")}:</strong>{" "}
												{viewData.to_clinic_admin?.[0]?.clinicName || "-"}
											</p>
											<p className="mb-0">
												<strong>{t("Pharmacy")}:</strong>{" "}
												{viewData.to_pharmacy_details?.[0]?.pharmacyName || "-"}
											</p>
										</CardBody>
									</Card>
								</Col>
							</Row>

							<h6 className="fw-bold mb-2">{t("Transferred Items")}</h6>
							<Table bordered hover striped>
								<thead>
									<tr>
										<th>#</th>
										<th>{t("From")}</th>
										<th>{t("To")}</th>
										<th>{t("Items")} ({t("Batch No.")})</th>
										<th>{t("Type")}</th>
										<th>{t("Status")}</th>
										<th>{t("Notes")}</th>
										<th>{t("Date")}</th>
										<th className="text-center">{t("Action")}</th>
									</tr>
								</thead>
								<tbody>
									{transfers.length > 0 ? (
										transfers.map((transfer, idx) => (
											<tr key={transfer._id}>
												<td>{(page - 1) * size + idx + 1}</td>
												<td>
													{renderPharmacyWithClinic(
														transfer.from_pharmacy_details?.[0],
														transfer.from_clinic_admin?.[0],
														true
													)}
												</td>
												<td>
													{renderPharmacyWithClinic(
														transfer.to_pharmacy_details?.[0],
														transfer.to_clinic_admin?.[0],
														false
													)}
												</td>
												<td>
													<span
														className="truncate-text"
														style={{ display: "inline-block", maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
														title={transfer.transferred_items
															.map((i) => `${i.medicine_name} (${i.batch_number})`)
															.join(", ")}
													>
														{transfer.transferred_items
															.map((i) => `${i.medicine_name} (${i.batch_number})`)
															.join(", ")}
													</span>
												</td>
												<td>{getTransferType(transfer)}</td>
												<td>{getStatusBadge(transfer.status)}</td>
												<td>
													<span
														className="truncate-text"
														id={`note-${transfer._id}`}
														title={transfer.notes || "-"}
													>
														{transfer.notes || "-"}
													</span>
												</td>
												<td>{formatDate(transfer.createdAt)}</td>

												<td className="text-center">
													<button
														className="success-action-button me-1"
														id={`ViewToolTip-${transfer._id}`}
														ref={(el) =>
															(eyeButtonRefs.current[transfer._id] = el)
														}
														onClick={() => handleView(transfer)}
													>
														<i
															className="fa fa-eye"
															style={{ fontSize: "18px" }}
														></i>
														<ToolTip
															attrToolTip={{
																placement: "top",
																isOpen: viewTooltipOpen[transfer._id] || false,
																target: `ViewToolTip-${transfer._id}`,
																toggle: () => toggleViewTooltip(transfer._id),
															}}
														>
															<strong>{t("View")}</strong>
														</ToolTip>
													</button>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="9" className="p-3 text-center text-muted">
												{t("No transfers found")}
											</td>
										</tr>
									)}
								</tbody>
							</Table>

							<Row className="mt-4">
								<Col md="3" className="mb-3">
									<strong>{t("Status")}:</strong>{" "}
									<span
										className={`badge ${viewData.status === "pending"
											? "bg-warning text-dark"
											: viewData.status === "completed"
												? "bg-success"
												: "bg-danger"
											}`}
									>
										{viewData.status}
									</span>
								</Col>
								<Col md="9">
									<strong>{t("Notes")}:</strong>
									<div className="border rounded p-2 bg-body-tertiary mt-1">
										{viewData.notes || t("No notes provided")}
									</div>
								</Col>
							</Row>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setViewModal(false)}>
						<i className="fa fa-times me-1"></i> {t("Close")}
					</Button>
				</ModalFooter>
			</Modal> */}

		</Fragment >
	);
};

export default TransferHistory;
